import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createBusiness } from '../services/api';
import { useBusiness } from '../context/BusinessContext';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshBusinesses } = useBusiness();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    timezone: 'America/New_York',
    greeting: '',
    transferNumber: '',
    businessHours: {
      monday: { open: '09:00', close: '17:00', closed: false },
      tuesday: { open: '09:00', close: '17:00', closed: false },
      wednesday: { open: '09:00', close: '17:00', closed: false },
      thursday: { open: '09:00', close: '17:00', closed: false },
      friday: { open: '09:00', close: '17:00', closed: false },
      saturday: { open: '09:00', close: '17:00', closed: true },
      sunday: { open: '09:00', close: '17:00', closed: true },
    },
    services: [{ id: '1', name: '', description: '', duration: 60, price: undefined }],
  });

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await createBusiness({
        ...formData,
        greeting:
          formData.greeting ||
          `Hello, thank you for calling ${formData.name}. How may I help you today?`,
        services: formData.services.filter((s) => s.name),
        faqs: [],
      });
      await refreshBusinesses();
      navigate('/');
    } catch (error) {
      console.error('Error creating business:', error);
      alert('Failed to create business');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Set Up Your AI Receptionist</h1>
          <p className="text-gray-600 mt-2">
            Let's get your business ready to answer calls
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  s <= step
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`w-16 h-1 ${
                    s < step ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="card">
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Basic Information</h2>
              <div>
                <label className="label">Business Name *</label>
                <input
                  type="text"
                  required
                  className="input"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Acme Dental Clinic"
                />
              </div>
              <div>
                <label className="label">Twilio Phone Number *</label>
                <input
                  type="tel"
                  required
                  className="input"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, phoneNumber: e.target.value })
                  }
                  placeholder="+12025551234"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your Twilio phone number that will receive calls
                </p>
              </div>
              <div>
                <label className="label">Timezone</label>
                <select
                  className="input"
                  value={formData.timezone}
                  onChange={(e) =>
                    setFormData({ ...formData, timezone: e.target.value })
                  }
                >
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>
              <div>
                <label className="label">Transfer Number (optional)</label>
                <input
                  type="tel"
                  className="input"
                  value={formData.transferNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, transferNumber: e.target.value })
                  }
                  placeholder="+12025559999"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Where to transfer calls when a human is requested
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleNext}
                  disabled={!formData.name || !formData.phoneNumber}
                  className="btn-primary"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Business Hours</h2>
              <div className="space-y-4">
                {Object.entries(formData.businessHours).map(([day, hours]) => (
                  <div key={day} className="flex items-center gap-4">
                    <div className="w-24 capitalize">{day}</div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!hours.closed}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            businessHours: {
                              ...formData.businessHours,
                              [day]: { ...hours, closed: !e.target.checked },
                            },
                          })
                        }
                        className="h-4 w-4 text-primary-600 rounded"
                      />
                      Open
                    </label>
                    {!hours.closed && (
                      <>
                        <input
                          type="time"
                          className="input w-auto"
                          value={hours.open}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              businessHours: {
                                ...formData.businessHours,
                                [day]: { ...hours, open: e.target.value },
                              },
                            })
                          }
                        />
                        <span>to</span>
                        <input
                          type="time"
                          className="input w-auto"
                          value={hours.close}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              businessHours: {
                                ...formData.businessHours,
                                [day]: { ...hours, close: e.target.value },
                              },
                            })
                          }
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between">
                <button onClick={handleBack} className="btn-secondary">
                  Back
                </button>
                <button onClick={handleNext} className="btn-primary">
                  Next
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Services</h2>
              <p className="text-gray-600">
                Add the services you offer (you can add more later)
              </p>
              <div className="space-y-4">
                {formData.services.map((service, index) => (
                  <div key={service.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Service Name</label>
                        <input
                          type="text"
                          className="input"
                          value={service.name}
                          onChange={(e) => {
                            const newServices = [...formData.services];
                            newServices[index] = { ...service, name: e.target.value };
                            setFormData({ ...formData, services: newServices });
                          }}
                          placeholder="Dental Cleaning"
                        />
                      </div>
                      <div>
                        <label className="label">Duration (minutes)</label>
                        <input
                          type="number"
                          className="input"
                          value={service.duration}
                          onChange={(e) => {
                            const newServices = [...formData.services];
                            newServices[index] = {
                              ...service,
                              duration: parseInt(e.target.value),
                            };
                            setFormData({ ...formData, services: newServices });
                          }}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="label">Description</label>
                        <input
                          type="text"
                          className="input"
                          value={service.description}
                          onChange={(e) => {
                            const newServices = [...formData.services];
                            newServices[index] = {
                              ...service,
                              description: e.target.value,
                            };
                            setFormData({ ...formData, services: newServices });
                          }}
                          placeholder="Regular teeth cleaning and checkup"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  setFormData({
                    ...formData,
                    services: [
                      ...formData.services,
                      {
                        id: Date.now().toString(),
                        name: '',
                        description: '',
                        duration: 60,
                        price: undefined,
                      },
                    ],
                  })
                }
                className="btn-secondary"
              >
                Add Another Service
              </button>
              <div className="flex justify-between">
                <button onClick={handleBack} className="btn-secondary">
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Creating...' : 'Complete Setup'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
